import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isValidSignatureDataUrl, buildWatermarkText } from "@dataroom/shared";
import { api, apiUrl, ensureDeviceFingerprint } from "../lib/api";
import { downloadDocument } from "../lib/fetchDocument";
import { formatViewerGateError } from "../lib/viewerGateErrors";
import { EmptyState } from "../components/EmptyState";
import { BrandLoader } from "../components/BrandLoader";
import { useDelayedLoader } from "../hooks/useDelayedLoader";
import { GateStepper } from "../components/GateStepper";
import { TurnstileWidget, turnstileEnabled } from "../components/TurnstileWidget";
import { FileTypeIcon, IconClose, IconDownload, IconFolder, IconSearch } from "../components/Icons";
import { NdaSignPad } from "../components/NdaSignPad";
import { ViewerPortalHeader, DEFAULT_VIEWER_LOGO } from "../components/ViewerPortalHeader";
import { ViewerFolderTree } from "../components/ViewerFolderTree";
import {
  clearViewerSession,
  getViewerEmail,
  getViewerSessionId,
  getViewerViewId,
  setViewerSession,
  setViewerViewId,
} from "../lib/viewerSession";
import { sanitizePreviewHtml } from "../lib/sanitizeHtml";

type GateMeta = {
  roomName: string;
  welcomeMessage?: string;
  bannerUrl?: string;
  logoUrl?: string;
  recipientEmailMasked?: string | null;
  gates: string[];
  watermarkEnabled: boolean;
  allowDownload: boolean;
  downloadPolicy?: string;
  branding?: { tokens_json?: string; tagline?: string };
};

type Doc = {
  id: string;
  filename: string;
  folder_id: string | null;
  mime_type?: string;
  isNew?: boolean;
  page_count?: number;
  display_name?: string;
  visitor_description?: string;
  folder_name?: string;
  folder_path?: string;
  snippet?: string;
  matchType?: string;
};

function docLabel(d: Doc) {
  return d.display_name?.trim() || d.filename;
}

function docHaystack(d: Doc) {
  return `${d.filename} ${d.display_name ?? ""} ${d.visitor_description ?? ""}`.toLowerCase();
}

type Folder = { id: string; name: string; path: string; parent_id?: string | null };

function fileCountLabel(n: number) {
  return `${n} ${n === 1 ? "item" : "items"}`;
}

function portalSubtitle(meta: GateMeta | null) {
  return meta?.branding?.tagline?.trim() || meta?.welcomeMessage?.trim() || undefined;
}

export function ViewerPage() {
  const { token = "" } = useParams();
  const privacyPath = `/v/${encodeURIComponent(token)}/privacy`;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState<GateMeta | null>(null);
  const [gate, setGate] = useState<"loading" | "email_verify" | "email_pending" | "password" | "nda" | "content" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState(() => getViewerSessionId(token));
  const [viewId, setViewId] = useState(() => getViewerViewId(token));
  const [email, setEmail] = useState(() => getViewerEmail(token));
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [ndaTitle, setNdaTitle] = useState("");
  const [ndaBody, setNdaBody] = useState("");
  const [fullName, setFullName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [documents, setDocuments] = useState<Doc[]>([]);
  const [activeDoc, setActiveDoc] = useState<string | null>(null);
  const [textView, setTextView] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [mode, setMode] = useState<"pdf" | "text">("pdf");
  const [busy, setBusy] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [textLoading, setTextLoading] = useState(false);
  const showGateLoader = useDelayedLoader(gate === "loading");
  const showTextLoader = useDelayedLoader(textLoading);
  const [homeBrowseFolderId, setHomeBrowseFolderId] = useState<string | null>(null);
  const [navQuery, setNavQuery] = useState("");
  const [homeFilter, setHomeFilter] = useState<"all" | "recent">("all");
  const [screen, setScreen] = useState<"home" | "document">("home");
  const [trustCollapsed, setTrustCollapsed] = useState(false);
  const [searchHits, setSearchHits] = useState<Doc[] | null>(null);
  const [searching, setSearching] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const eventBuffer = useMemo(() => [] as Array<{ documentId: string; pageNumber: number; dwellMs: number; event: string }>, []);
  const hydratedRef = useRef(false);
  const emailRef = useRef(email);
  emailRef.current = email;
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Rebind all gate state when the invite token changes (SPA navigation /v/A → /v/B).
  useEffect(() => {
    setSessionId(getViewerSessionId(token));
    setViewId(getViewerViewId(token));
    setEmail(getViewerEmail(token));
    setGate("loading");
    setError(null);
    setMeta(null);
    setFolders([]);
    setDocuments([]);
    setActiveDoc(null);
    setTextView(null);
    setPendingEmail(null);
    setPassword("");
    setName("");
    setNdaTitle("");
    setNdaBody("");
    setFullName("");
    setSignature(null);
    setTurnstileToken(null);
    setBusy(false);
    setFilesLoading(false);
    setResendCooldown(0);
    setNavOpen(false);
    setScreen("home");
    setHomeBrowseFolderId(null);
    setSearchHits(null);
    hydratedRef.current = false;
  }, [token]);

  // New token / session must re-fetch the file list; do not keep a stale hydrated flag.
  useEffect(() => {
    hydratedRef.current = false;
  }, [token, sessionId]);

  useEffect(() => {
    const main = mainRef.current;
    if (!main || screen !== "document") {
      setTrustCollapsed(false);
      return;
    }
    const onScroll = () => setTrustCollapsed(main.scrollTop > 48);
    main.addEventListener("scroll", onScroll, { passive: true });
    return () => main.removeEventListener("scroll", onScroll);
  }, [screen, activeDoc]);

  const flushEvents = useCallback(
    async (keepalive = false) => {
      if (!sessionId || !eventBuffer.length) return;
      const batch = eventBuffer.splice(0, eventBuffer.length);
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Viewer-Session": sessionId,
      };
      const fp = localStorage.getItem("deviceFp");
      if (fp) headers["X-Device-Fingerprint"] = fp;
      await fetch(apiUrl(`/api/v1/viewer/${token}/events`), {
        method: "POST",
        headers,
        body: JSON.stringify({ events: batch }),
        keepalive,
      }).catch(() => undefined);
    },
    [eventBuffer, sessionId, token],
  );

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") flushEvents(true).catch(() => undefined);
    };
    const onUnload = () => flushEvents(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [flushEvents]);

  useEffect(() => {
    document.body.style.overflow = navOpen ? "hidden" : "";
    if (!navOpen) return;
    const nav = document.querySelector<HTMLElement>(".viewer-nav");
    const prev = document.activeElement as HTMLElement | null;
    const focusables = () =>
      [...(nav?.querySelectorAll<HTMLElement>("button,[href],input,select,textarea,[tabindex]:not([tabindex='-1'])") ?? [])].filter(
        (el) => !el.hasAttribute("disabled"),
      );
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setNavOpen(false);
        return;
      }
      if (e.key !== "Tab" || !nav) return;
      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [navOpen]);

  useEffect(() => {
    let cancelled = false;
    ensureDeviceFingerprint();
    api<GateMeta>(`/api/v1/viewer/${token}`, { admin: false })
      .then((m) => {
        if (cancelled) return;
        setMeta(m);
        if (sessionId) {
          // Promote from loading/email gates only. Do not stomp password/nda after applySession.
          setGate((g) =>
            g === "loading" || g === "email_pending" || g === "email_verify" || g === "error" ? "content" : g,
          );
        } else {
          // Stale meta (started before magic-link confirm) must not stomp password/nda/content.
          setGate((g) =>
            g === "loading" || g === "error" ? ((m.gates[0] as typeof gate) || "content") : g,
          );
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = formatViewerGateError(String(e.message || e));
        // Do not kick password/nda/content to full error on a stale meta failure.
        setGate((g) => {
          if (g === "loading" || g === "error") {
            setError(msg);
            return "error";
          }
          return g;
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token, sessionId]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = window.setTimeout(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [resendCooldown]);

  const applySession = useCallback(
    (res: { sessionId: string; viewId: string; nextGate: string; email?: string }, forToken: string) => {
      const nextEmail = (res.email ?? (forToken === tokenRef.current ? emailRef.current : "")).trim();
      setViewerSession(forToken, {
        sessionId: res.sessionId,
        viewId: res.viewId,
        email: nextEmail || undefined,
      });
      // In-flight success for a prior invite must not stomp the current SPA token.
      if (forToken !== tokenRef.current) return;
      if (nextEmail) setEmail(nextEmail);
      setSessionId(res.sessionId);
      setViewId(res.viewId);
      setGate(res.nextGate as typeof gate);
      setPendingEmail(null);
      setError(null);
    },
    [],
  );

  const confirmEmailVerify = useCallback(
    async (code: string) => {
      const forToken = token;
      setBusy(true);
      setError(null);
      const clearVerifyParam = () => {
        if (forToken !== tokenRef.current) return;
        const next = new URLSearchParams(searchParams);
        if (!next.has("verify")) return;
        next.delete("verify");
        setSearchParams(next, { replace: true });
      };
      try {
        const res = await api<{ sessionId: string; viewId: string; nextGate: string; email?: string }>(
          `/api/v1/viewer/${forToken}/confirm-email-verify`,
          {
            method: "POST",
            admin: false,
            body: JSON.stringify({ code }),
          },
        );
        applySession(res, forToken);
        clearVerifyParam();
      } catch (e) {
        if (forToken !== tokenRef.current) return;
        setError(formatViewerGateError(String((e as Error).message || e)));
        setGate("email_verify");
        clearVerifyParam();
      } finally {
        if (forToken === tokenRef.current) setBusy(false);
      }
    },
    [applySession, searchParams, setSearchParams, token],
  );

  useEffect(() => {
    const t = setInterval(() => {
      flushEvents().catch(() => undefined);
    }, 4000);
    return () => clearInterval(t);
  }, [flushEvents]);

  const navSearching = Boolean(navQuery.trim());

  useEffect(() => {
    const q = navQuery.trim();
    if (!sessionId || q.length < 2) {
      setSearchHits(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = window.setTimeout(() => {
      api<{ results: Doc[] }>(`/api/v1/viewer/${token}/search?q=${encodeURIComponent(q)}`, {
        admin: false,
        sessionId,
      })
        .then((res) => setSearchHits(res.results ?? []))
        .catch(() => setSearchHits([]))
        .finally(() => setSearching(false));
    }, 280);
    return () => window.clearTimeout(t);
  }, [navQuery, sessionId, token]);

  const homeBrowseFolders = useMemo(
    () => folders.filter((f) => (f.parent_id ?? null) === homeBrowseFolderId).sort((a, b) => a.name.localeCompare(b.name)),
    [folders, homeBrowseFolderId],
  );

  const homeBrowseDocs = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    let pool = documents.filter((d) => d.folder_id === homeBrowseFolderId);
    if (q) pool = pool.filter((d) => docHaystack(d).includes(q));
    if (homeFilter === "recent") {
      pool = [...pool].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    } else {
      pool.sort((a, b) => a.filename.localeCompare(b.filename));
    }
    return pool;
  }, [documents, homeBrowseFolderId, navQuery, homeFilter]);

  const homeBrowseTitle = useMemo(() => {
    if (!homeBrowseFolderId) return meta?.roomName ?? "Space overview";
    return folders.find((f) => f.id === homeBrowseFolderId)?.name ?? "Folder";
  }, [homeBrowseFolderId, folders, meta?.roomName]);

  const homeItemCount = homeBrowseFolders.length + homeBrowseDocs.length;

  const selectFolder = useCallback((folderId: string) => {
    setHomeBrowseFolderId(folderId);
    setScreen("home");
    setActiveDoc(null);
    setMode("pdf");
    setTextView(null);
    setNavOpen(false);
  }, []);

  const openDocPreview = useCallback(
    (docId: string) => {
      if (!sessionId) {
        setError("Session expired. Refresh the Space and try again.");
        return;
      }
      void flushEvents();
      setNavOpen(false);
      const url = `/v/${token}/doc/${docId}?sid=${encodeURIComponent(sessionId)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [flushEvents, sessionId, token],
  );

  const selectDoc = openDocPreview;

  const goHome = useCallback(() => {
    void flushEvents();
    setHomeBrowseFolderId(null);
    setMode("pdf");
    setTextView(null);
    setNavOpen(false);
    // Optimistic: route effect confirms after navigate.
    setActiveDoc(null);
    setScreen("home");
    navigate(`/v/${token}`, { replace: true });
  }, [flushEvents, navigate, token]);

  // Fetch the file list once per content session. Do NOT re-sync screen/activeDoc
  // here: closing preview used to change activeDoc, recreate this callback, re-run
  // the effect, and reopen the document from a stale routeDocId (stuck animation).
  const loadContent = useCallback(
    async (sid: string) =>
      api<{ folders: Folder[]; documents: Doc[]; viewId: string }>(`/api/v1/viewer/${token}/files`, {
        admin: false,
        sessionId: sid,
      }),
    [token],
  );

  useEffect(() => {
    if (gate !== "content" || !sessionId || hydratedRef.current) return;
    let cancelled = false;
    setFilesLoading(true);
    loadContent(sessionId)
      .then((res) => {
        if (cancelled) return;
        setFolders(res.folders);
        setDocuments(res.documents);
        if (res.viewId) {
          setViewId(res.viewId);
          setViewerViewId(token, res.viewId);
        }
        // Invalid deep links are handled by StandalonePreviewPage.
        hydratedRef.current = true;
        setGate("content");
      })
      .catch((e) => {
        if (cancelled) return;
        // On a restored session we optimistically show content; if the server
        // still requires a gate (e.g. NDA not yet signed), route to that gate
        // instead of stranding the visitor on a broken content screen.
        const msg = String((e as Error)?.message || e);
        if (msg === "nda_required") setGate("nda");
        else if (msg === "password_required") setGate("password");
        else if (
          msg === "email_required" ||
          msg === "session_expired" ||
          msg === "session_mismatch" ||
          msg === "fingerprint_mismatch"
        ) {
          clearViewerSession(token, { keepEmail: msg !== "fingerprint_mismatch" && msg !== "session_mismatch" });
          setSessionId("");
          setViewId("");
          if (msg === "fingerprint_mismatch" || msg === "session_mismatch") setEmail("");
          hydratedRef.current = false;
          setError(
            msg === "fingerprint_mismatch" || msg === "session_mismatch" || msg === "session_expired"
              ? formatViewerGateError(msg)
              : null,
          );
          setGate("email_verify");
        } else {
          setError(formatViewerGateError(msg));
          setGate("error");
        }
      })
      .finally(() => {
        if (!cancelled) setFilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gate, sessionId, loadContent, token]);

  useEffect(() => {
    if (gate !== "nda") return;
    let cancelled = false;
    api<{ nda: { title: string; body_html: string } | null }>(`/api/v1/viewer/${token}/nda`, { admin: false })
      .then((r) => {
        if (cancelled) return;
        setNdaTitle(r.nda?.title ?? "Non-Disclosure Agreement");
        setNdaBody(sanitizePreviewHtml(r.nda?.body_html ?? "<p>Please confirm you will keep materials confidential.</p>"));
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [gate, token]);

  const requestEmailVerify = async () => {
    const forToken = token;
    if (turnstileEnabled() && !turnstileToken) {
      setError("Complete the verification check below.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ sent?: boolean; sessionId?: string; viewId?: string; nextGate?: string; email?: string }>(
        `/api/v1/viewer/${forToken}/request-email-verify`,
        {
          method: "POST",
          admin: false,
          body: JSON.stringify({ email, name: name || undefined, turnstileToken: turnstileToken ?? undefined }),
        },
      );
      if (res.sessionId && res.viewId && res.nextGate) {
        applySession(
          {
            sessionId: res.sessionId,
            viewId: res.viewId,
            nextGate: res.nextGate,
            email: res.email ?? email,
          },
          forToken,
        );
        return;
      }
      if (forToken !== tokenRef.current) return;
      setPendingEmail(res.email ?? email);
      setGate("email_pending");
      setResendCooldown(60);
    } catch (e) {
      if (forToken !== tokenRef.current) return;
      setError(formatViewerGateError(String((e as Error).message || e)));
    } finally {
      // Turnstile tokens are single-use: remount the widget for a fresh challenge.
      setTurnstileToken(null);
      setTurnstileNonce((n) => n + 1);
      if (forToken === tokenRef.current) setBusy(false);
    }
  };

  const verifyEmail = requestEmailVerify;

  const verifyPassword = async () => {
    const forToken = token;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ nextGate: string }>(`/api/v1/viewer/${forToken}/verify-password`, {
        method: "POST",
        admin: false,
        sessionId,
        body: JSON.stringify({ password }),
      });
      if (forToken !== tokenRef.current) return;
      setGate(res.nextGate as typeof gate);
    } catch (e) {
      if (forToken !== tokenRef.current) return;
      setError(formatViewerGateError(String((e as Error).message || e)));
    } finally {
      if (forToken === tokenRef.current) setBusy(false);
    }
  };

  const acceptNda = async () => {
    const forToken = token;
    if (!isValidSignatureDataUrl(signature)) {
      setError("Draw your signature to continue.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ nextGate: string }>(`/api/v1/viewer/${forToken}/nda/accept`, {
        method: "POST",
        admin: false,
        sessionId,
        body: JSON.stringify({ fullName: fullName.trim(), signatureDataUrl: signature, agreed: true }),
      });
      if (forToken !== tokenRef.current) return;
      setGate(res.nextGate as typeof gate);
    } catch (e) {
      if (forToken !== tokenRef.current) return;
      setError(formatViewerGateError(String((e as Error).message || e)));
    } finally {
      if (forToken === tokenRef.current) setBusy(false);
    }
  };

  const gateFooter = (
    <>
      <p className="muted viewer-gate__footer">Your email may appear as a watermark on documents.</p>
      <Link to={privacyPath}>Privacy notice</Link>
    </>
  );

  const openTextView = async (docId: string) => {
    setTextLoading(true);
    setError(null);
    try {
      const res = await api<{ text: string }>(`/api/v1/viewer/${token}/files/${docId}/text`, {
        admin: false,
        sessionId,
      });
      setTextView(res.text);
      setMode("text");
      setActiveDoc(docId);
      setScreen("document");
      setNavOpen(false);
    } catch (e) {
      setError(formatViewerGateError(String((e as Error).message || e)));
    } finally {
      setTextLoading(false);
    }
  };

  const downloadPath = activeDoc ? `/api/v1/viewer/${token}/files/${activeDoc}/download` : null;
  const activeMeta = documents.find((d) => d.id === activeDoc);
  const visitorWatermarkText = meta?.watermarkEnabled
    ? buildWatermarkText({ email, viewId, timestamp: new Date().toISOString() })
    : undefined;

  const downloadActive = async () => {
    if (!downloadPath || !activeDoc) return;
    await downloadDocument(downloadPath, activeMeta?.filename || "download", {
      sessionId,
      watermarkText: visitorWatermarkText,
    });
  };

  const closeTextView = () => {
    void flushEvents();
    const folderId = activeMeta?.folder_id ?? null;
    setHomeBrowseFolderId(folderId);
    setMode("pdf");
    setTextView(null);
    setNavOpen(false);
    setActiveDoc(null);
    setScreen("home");
  };

  const brandPrimary =
    (() => {
      try {
        return JSON.parse(meta?.branding?.tokens_json || "{}").primary as string | undefined;
      } catch {
        return undefined;
      }
    })() ?? "var(--portal-brand-primary)";

  const logoSrc = meta?.logoUrl || DEFAULT_VIEWER_LOGO;

  const downloadDoc = async (doc: Doc) => {
    const path = `/api/v1/viewer/${token}/files/${doc.id}/download`;
    await downloadDocument(path, doc.filename, { sessionId, watermarkText: visitorWatermarkText });
  };

  const gateHeader = (
    <ViewerPortalHeader
      variant="gate"
      roomName={meta?.roomName ?? "Secure Space"}
      subtitle={
        meta?.recipientEmailMasked
          ? `Invited: ${meta.recipientEmailMasked}`
          : meta?.welcomeMessage || "Enter your email to continue."
      }
      bannerUrl={meta?.bannerUrl}
      logoUrl={logoSrc}
      brandPrimary={brandPrimary}
    />
  );

  if (gate === "error") {
    return (
      <div className="viewer-shell">
        <ViewerPortalHeader
          variant="gate"
          roomName={meta?.roomName ?? "Secure Space"}
          subtitle="This secure link cannot be opened."
          bannerUrl={meta?.bannerUrl}
          logoUrl={logoSrc}
          brandPrimary={brandPrimary}
        />
        <div className="viewer-gate stack">
          <h1 className="page-title" style={{ marginBottom: 8 }}>Link unavailable</h1>
          <p role="alert" className="muted" style={{ margin: 0 }}>
            {error ?? "This secure link is invalid, expired, or revoked."}
          </p>
          <Link to={privacyPath}>Privacy notice</Link>
        </div>
      </div>
    );
  }

  if (gate === "loading") {
    return (
      <div className="viewer-shell">
        <ViewerPortalHeader
          variant="gate"
          roomName={meta?.roomName ?? "Secure Space"}
          subtitle="Opening secure link…"
          bannerUrl={meta?.bannerUrl}
          logoUrl={logoSrc}
          brandPrimary={brandPrimary}
        />
        <div className="viewer-gate card brand-loader-inline" style={{ padding: 16 }}>
          {showGateLoader ? <BrandLoader label="Opening secure room…" /> : null}
        </div>
      </div>
    );
  }

  const verifyCode = searchParams.get("verify");
  if (gate === "email_verify" && verifyCode) {
    // Wait for a click: mail security scanners open links and run scripts, which would spend the one-time code.
    return (
      <div className="viewer-shell">
        {gateHeader}
        <div className="stack viewer-gate">
          <GateStepper gates={meta?.gates ?? ["email_verify"]} current="email_pending" />
          <h2 className="page-title" style={{ marginTop: 0 }}>Continue to the Space</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            You opened your sign-in link. Continue to finish signing in.
          </p>
          {error && <p role="alert">{error}</p>}
          <button type="button" disabled={busy} onClick={() => confirmEmailVerify(verifyCode).catch(() => undefined)}>
            {busy ? "Opening..." : "Continue"}
          </button>
          {gateFooter}
        </div>
      </div>
    );
  }

  if (gate === "email_verify") {
    return (
      <div className="viewer-shell">
        {gateHeader}
        <form
          className="stack viewer-gate"
          onSubmit={(e) => {
            e.preventDefault();
            verifyEmail().catch((err) => setError(formatViewerGateError(String(err.message || err))));
          }}
        >
          <GateStepper gates={meta?.gates ?? ["email_verify"]} current="email_verify" />
          <h2 className="page-title" style={{ marginTop: 0 }}>Enter your email</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            {meta?.recipientEmailMasked
              ? `Access is restricted to ${meta.recipientEmailMasked}. Confirm it to continue.`
              : "Enter your work email to continue."}
          </p>
          <label>
            Work email
            <input
              className="input"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            Your name
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
          </label>
          <TurnstileWidget key={turnstileNonce} onToken={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />
          {error && <p role="alert">{error}</p>}
          <button type="submit" disabled={busy || (turnstileEnabled() && !turnstileToken)}>
            {busy ? "Opening…" : "Continue"}
          </button>
          {gateFooter}
        </form>
      </div>
    );
  }

  if (gate === "email_pending") {
    return (
      <div className="viewer-shell">
        {gateHeader}
        <div className="stack viewer-gate">
          <GateStepper gates={meta?.gates ?? ["email_verify"]} current="email_pending" />
          <h2 className="page-title" style={{ marginTop: 0 }}>Check your inbox</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            We sent a verification link to <strong>{pendingEmail ?? email}</strong>. Open it on this device to continue.
          </p>
          {resendCooldown === 0 && (
            <TurnstileWidget key={turnstileNonce} onToken={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />
          )}
          {error && <p role="alert">{error}</p>}
          <button
            type="button"
            className="secondary"
            disabled={busy || resendCooldown > 0 || (turnstileEnabled() && !turnstileToken)}
            onClick={() => requestEmailVerify().catch((err) => setError(formatViewerGateError(String(err.message || err))))}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend link"}
          </button>
          <button type="button" className="ghost" onClick={() => { setGate("email_verify"); setError(null); }}>
            Use a different email
          </button>
          {gateFooter}
        </div>
      </div>
    );
  }

  if (gate === "password") {
    return (
      <div className="viewer-shell">
        {gateHeader}
        <form
          className="stack viewer-gate"
          onSubmit={(e) => {
            e.preventDefault();
            verifyPassword().catch((err) => setError(formatViewerGateError(String(err.message || err))));
          }}
        >
          <GateStepper gates={meta?.gates ?? []} current="password" />
          <h2 className="page-title" style={{ marginTop: 0 }}>Password required</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            This Space is protected. Enter the password you were given.
          </p>
          <label>
            Password
            <input className="input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          {error && <p role="alert">{error}</p>}
          <button type="submit" disabled={busy}>{busy ? "Unlocking…" : "Unlock"}</button>
          {gateFooter}
        </form>
      </div>
    );
  }

  if (gate === "nda") {
    return (
      <div className="viewer-shell">
        {gateHeader}
        <article className="stack viewer-gate">
          <GateStepper gates={meta?.gates ?? []} current="nda" />
          <h2 className="page-title" style={{ marginTop: 0 }}>{ndaTitle}</h2>
          <div dangerouslySetInnerHTML={{ __html: ndaBody }} />
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              if (!fullName.trim()) return;
              if (!isValidSignatureDataUrl(signature)) {
                setError("Draw your signature to continue.");
                return;
              }
              acceptNda().catch((err) => setError(formatViewerGateError(String(err.message || err))));
            }}
          >
            <label>
              Full legal name
              <input className="input" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </label>
            <NdaSignPad onChange={setSignature} />
            {error && <p role="alert">{error}</p>}
            <button type="submit" disabled={!fullName.trim() || !isValidSignatureDataUrl(signature) || busy}>
              {busy ? "Signing…" : "Sign & continue"}
            </button>
            {gateFooter}
          </form>
        </article>
      </div>
    );
  }

  return (
    <div className={`viewer-app${navOpen ? " nav-open" : ""}`}>
      {navOpen ? (
        <button type="button" className="viewer-backdrop" aria-label="Close documents" onClick={() => setNavOpen(false)} />
      ) : null}
      <aside className="viewer-nav" aria-label="Documents">
        <p className="viewer-nav__label">Browse</p>
        {screen === "document" ? (
          <label className="search-field" style={{ margin: "0 0 10px", width: "100%" }}>
            <IconSearch className="search-field__icon" />
            <input
              className="input"
              placeholder="Search files"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              aria-label="Search files"
            />
          </label>
        ) : null}
        <ViewerFolderTree
          folders={folders}
          documents={documents}
          activeDocId={activeDoc}
          activeFolderId={homeBrowseFolderId}
          screen={screen}
          query={navQuery}
          searchHits={searchHits}
          searching={searching}
          onSelectDoc={selectDoc}
          onSelectFolder={selectFolder}
          onGoHome={goHome}
          onCloseNav={() => setNavOpen(false)}
        />
        {viewId ? (
          <p className="muted" style={{ fontSize: 12, marginTop: 12, padding: "0 6px" }}>
            View ID {viewId}
          </p>
        ) : null}
      </aside>

      <div className="viewer-portal">
        <ViewerPortalHeader
          variant={screen === "document" ? "compact" : "home"}
          roomName={meta?.roomName ?? "Secure Space"}
          subtitle={portalSubtitle(meta)}
          bannerUrl={meta?.bannerUrl}
          logoUrl={logoSrc}
          brandPrimary={brandPrimary}
          viewerLabel={email || meta?.recipientEmailMasked || "verified visitor"}
          viewId={viewId}
          watermarkEnabled={meta?.watermarkEnabled}
          searchQuery={navQuery}
          onSearchChange={setNavQuery}
          showSearch={screen === "home"}
        />

        <main ref={mainRef} className={`viewer-main${screen === "document" ? " viewer-main--reading" : ""}`} aria-live="polite">
          {screen === "document" ? (
            <div className={`viewer-trust-bar${trustCollapsed ? " is-collapsed" : ""}`}>
              <span>
                Viewing as <strong>{email || meta?.recipientEmailMasked || "verified visitor"}</strong>
              </span>
              {viewId ? <span>View ID {viewId}</span> : null}
              {meta?.watermarkEnabled ? <span>Watermarked</span> : null}
            </div>
          ) : null}

          <div className="viewer-toolbar row toolbar" style={{ justifyContent: "space-between" }}>
            <div className="row toolbar">
              <button type="button" className="viewer-nav-toggle secondary" onClick={() => setNavOpen(true)}>
                Browse
              </button>
              {screen === "document" && mode === "text" ? (
                <>
                  <button type="button" className="secondary" onClick={goHome}>
                    Space overview
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => activeDoc && openDocPreview(activeDoc)}
                  >
                    Preview
                  </button>
                  <button type="button" className={mode === "text" ? "" : "secondary"} disabled>
                    Text
                  </button>
                  {meta?.allowDownload && activeDoc ? (
                    <button
                      type="button"
                      className="secondary"
                      onClick={() =>
                        downloadActive().catch((err) =>
                          setError(formatViewerGateError(String(err.message || err))),
                        )
                      }
                    >
                      Download
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
            <div className="row toolbar" style={{ gap: 12 }}>
              {screen === "document" && mode === "text" ? (
                <>
                  <button
                    type="button"
                    className="secondary viewer-close-preview"
                    onClick={closeTextView}
                    aria-label="Close text view and return to folder"
                    title="Close text view"
                  >
                    <IconClose aria-hidden />
                    Close
                  </button>
                  <Link to={privacyPath}>Privacy</Link>
                </>
              ) : null}
            </div>
          </div>

          {error ? <p role="alert">{error}</p> : null}

          {screen === "home" ? (
            <div className="viewer-home-wrap">
              {homeBrowseFolderId ? (
                <button
                  type="button"
                  className="ghost viewer-home-back"
                  onClick={() => {
                    const parent = folders.find((f) => f.id === homeBrowseFolderId)?.parent_id ?? null;
                    setHomeBrowseFolderId(parent);
                  }}
                >
                  ← Back
                </button>
              ) : null}

              <div className="viewer-home-toolbar">
                <button
                  type="button"
                  className="viewer-nav-toggle secondary viewer-home-toolbar__browse"
                  onClick={() => setNavOpen(true)}
                >
                  Browse
                </button>
                <div className="viewer-home-toolbar__tabs" role="tablist" aria-label="Filter files">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={homeFilter === "all"}
                    className={`viewer-home-toolbar__tab${homeFilter === "all" ? " is-active" : ""}`}
                    onClick={() => setHomeFilter("all")}
                  >
                    All files
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={homeFilter === "recent"}
                    className={`viewer-home-toolbar__tab${homeFilter === "recent" ? " is-active" : ""}`}
                    onClick={() => setHomeFilter("recent")}
                  >
                    Recently added
                  </button>
                </div>
                <Link className="viewer-home-toolbar__privacy" to={privacyPath}>
                  Privacy
                </Link>
              </div>

              {filesLoading ? (
                <BrandLoader label="Loading documents" />
              ) : documents.length === 0 ? (
                <EmptyState
                  headline="No documents yet"
                  body="The deal team has not published files to this Space yet."
                />
              ) : navSearching && !homeBrowseFolders.length && !homeBrowseDocs.length ? (
                <EmptyState headline="No matches" body={`No files match "${navQuery.trim()}".`} />
              ) : (
                <section className="viewer-home-list">
                  <header className="viewer-home-list__head">
                    <h2 className="viewer-home-list__title">{homeBrowseTitle}</h2>
                    <span className="viewer-home-list__count">{fileCountLabel(homeItemCount)}</span>
                  </header>
                  <ul className="viewer-home-list__items" role="list">
                    {homeBrowseFolders.map((folder) => {
                      const folderDocCount = documents.filter((d) => d.folder_id === folder.id).length;
                      const subfolderCount = folders.filter((f) => f.parent_id === folder.id).length;
                      const total = folderDocCount + subfolderCount;
                      return (
                        <li key={folder.id} className="viewer-home-list__item">
                          <button
                            type="button"
                            className="viewer-home-list__row viewer-home-list__row--folder"
                            onClick={() => selectFolder(folder.id)}
                          >
                            <span className="viewer-home-list__glyph" aria-hidden>
                              <IconFolder />
                            </span>
                            <span className="viewer-home-list__main">
                              <span className="viewer-home-list__name">{folder.name}</span>
                              <span className="viewer-home-list__meta">{fileCountLabel(total)}</span>
                            </span>
                            <span className="viewer-home-list__chevron" aria-hidden>›</span>
                          </button>
                        </li>
                      );
                    })}
                    {homeBrowseDocs.map((d) => (
                      <li key={d.id} className="viewer-home-list__item">
                        <div className="viewer-home-list__row viewer-home-list__row--file">
                          <button type="button" className="viewer-home-list__open" onClick={() => selectDoc(d.id)}>
                            <span className="viewer-home-list__glyph" aria-hidden>
                              <FileTypeIcon name={d.filename} />
                            </span>
                            <span className="viewer-home-list__main">
                              <span className="viewer-home-list__name">{docLabel(d)}</span>
                              <span className="viewer-home-list__meta">
                                {d.page_count ? `${d.page_count} pages` : null}
                                {d.display_name && d.display_name !== d.filename ? ` · ${d.filename}` : null}
                              </span>
                            </span>
                            {d.isNew ? <span className="badge">New</span> : null}
                          </button>
                          {meta?.allowDownload ? (
                            <button
                              type="button"
                              className="viewer-home-list__download ghost"
                              aria-label={`Download ${docLabel(d)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadDoc(d).catch((err) =>
                                  setError(formatViewerGateError(String((err as Error).message || err))),
                                );
                              }}
                            >
                              <IconDownload />
                              Save
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="viewer-home-list__download ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTextView(d.id).catch(() => undefined);
                            }}
                          >
                            Text
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          ) : mode === "text" && textLoading ? (
            <div className="card brand-loader-inline" style={{ padding: 16 }}>
              {showTextLoader ? <BrandLoader label="Loading text…" /> : null}
            </div>
          ) : mode === "text" && textView && activeMeta ? (
            <>
              <div className="viewer-doc-header">
                <div>
                  <strong>{docLabel(activeMeta)}</strong>
                  {activeMeta.visitor_description ? (
                    <p className="muted" style={{ margin: "4px 0 0", fontSize: 13 }}>{activeMeta.visitor_description}</p>
                  ) : null}
                </div>
              </div>
              <article className="card" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {textView}
              </article>
            </>
          ) : (
            <p className="muted" style={{ margin: "48px auto", textAlign: "center", maxWidth: 280 }}>
              Choose a file from the list
            </p>
          )}
        </main>
      </div>
    </div>
  );
}
